/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { getApplicableL2MyTeamModules } from 'Deer/components/navigation/secondary/MyTeam/MyTeamModules';
import { L3_PATHS } from 'Deer/constants/routes';
import { useViewFromQueryParam } from 'Deer/hooks/useViewFromQueryParam';
import { DeerBreadcrumbs } from 'Deer/intent/components/Layout/Layout';
import ScrollToTop from 'components/ScrollToTop';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  resetResultState,
  REVIEW_RESULT_VIEW,
  useCurrentProvider,
  useSelectedReviewResultView,
} from 'reducers/reviewResultSlice';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import {
  checkEnableCompanySupport,
  checkEnableESignReviewReport,
  checkIntentLandingPagesFeatureEnabled,
  checkNewNavEnabled,
  checkSimplifiedReviewCreationEnabled,
} from 'utils/helpers/featureFlags';

import { ArrowBack } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noReviews.svg';

import { REVIEW_CYCLE_EVENTS } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';
import { NETCHEX_ROLE, isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import { BreadCrumb, BreadcrumbItem, BreadcrumbLink } from 'uiToolkitV1/BreadCrumb';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader1, TextHeader2 } from 'uiToolkitV1/Typography';

import { REVIEW_TABLE_TYPES } from '../LandingPage/ReviewAudit';
import { PerformanceResultScore } from '../components/CongratsFooter/components/PerformanceScore';
import {
  ReviewResultPreviewHeaderNote,
  SwitchableReviewFormPreviewHeaderNote,
} from '../components/ReviewPreviewHeaderNote';
import { useReviewFormSidebarData } from '../components/Sidebar/hooks/dataHooks';
import { useReviewPreviewParams } from '../hooks/reviewPreviewHooks';
import useReviewResultParams from '../hooks/useReviewResultParams';
import CompetencyResult from './CompetencyResult/CompetencyResult';
import GoalResult from './GoalResult/GoalResult';
import OverallQuestionsResult from './OverallQuestionsResult/OverallQuestionsResult';
import RabbitResultAcknowledge from './RabbitResultAcknowledge';
import RabbitResultAcknowledgeInfo from './RabbitResultAcknowledgeInfo';
import RabbitResultDocusignAcknowledge from './RabbitResultDocusignAcknowledge';
import EmailPDFReport from './components/EmailPDFReport';
import ResultSpiderChart from './components/ResultSpiderChart';

const StyledPaper = styled(Box)`
  background: ${(props) => props.theme.colors.neutral.F5F7F9};
  border-radius: 0;
`;

const REDIRECTION_ROUTE_FOR_SECOND_LABEL = {
  [REVIEW_INTENTS.REVIEW_COMPLETION_STATUS]: {
    directReports: L3_PATHS.team.directReports.review.completionStatus,
    allReports: L3_PATHS.team.allReports.review.completionStatus,
    view: L3_PATHS.team.view.review.completionStatus,
    company: L3_PATHS.company.review.completionStatus,
  },
  [REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS]: {
    directReports: L3_PATHS.team.directReports.review.calibratedResults,
    allReports: L3_PATHS.team.allReports.review.calibratedResults,
    view: L3_PATHS.team.view.review.calibratedResults,
    company: L3_PATHS.company.review.calibratedResults,
  },
};

// eslint-disable-next-line consistent-return
const getSecondLabelTo = ({ source, intentType }) => {
  if (source === 'allReports') {
    return REDIRECTION_ROUTE_FOR_SECOND_LABEL[intentType]?.allReports;
  }
  if (source === 'directReports') {
    return REDIRECTION_ROUTE_FOR_SECOND_LABEL[intentType]?.directReports;
  }
  if (source === 'view') {
    return REDIRECTION_ROUTE_FOR_SECOND_LABEL[intentType]?.view;
  }
  if (source === 'company') {
    return REDIRECTION_ROUTE_FOR_SECOND_LABEL[intentType]?.company;
  }
};

const getBreadcrumbRoute = (source, history) => {
  switch (source) {
    case 'My Team':
      return {
        link: null,
        onClick: () => {
          history.goBack();
        },
        name: 'My Team',
      };
    case 'My Company':
      return {
        link: null,
        onClick: () => {
          history.goBack();
        },
        name: 'My Company',
      };
    case 'My Organization Tree':
      return {
        link: null,
        onClick: () => {
          history.goBack();
        },
        name: 'My Organization Tree',
      };
    case REVIEW_TABLE_TYPES.COMPLETED_BY_ME:
      return {
        link: checkNewNavEnabled()
          ? `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${REVIEW_TABLE_TYPES.COMPLETED_BY_ME}`
          : `${routes.performanceReview.audit}?activeTableId=${REVIEW_TABLE_TYPES.COMPLETED_BY_ME}`,
        onClick: null,
        name: 'Completed by me',
      };
    case REVIEW_TABLE_TYPES.MY_PAST_REVIEWS:
      return {
        link: checkNewNavEnabled()
          ? `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${REVIEW_TABLE_TYPES.MY_PAST_REVIEWS}`
          : `${routes.performanceReview.audit}?activeTableId=${REVIEW_TABLE_TYPES.MY_PAST_REVIEWS}`,
        onClick: null,
        name: 'My Past Reviews',
      };
    default:
      return {};
  }
};

const HeaderBreadCrumb = ({ isLoading, data, reviewCycleName, reviewNotVisible = false }) => {
  const { displayName } = data ?? {};
  const selectedView = useSelectedReviewResultView();
  const { source } = useParsedQuery();
  const history = useHistory();
  if (isLoading) {
    return null;
  }
  const isManagePageSource = ['My Company', 'My OrganizationTree', 'My Team'].includes(source);
  const { link, name, onClick } = getBreadcrumbRoute(source, history);
  return (
    <BreadCrumb>
      {!isManagePageSource && (
        <BreadcrumbItem>
          <BreadcrumbLink
            to={checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.index}
          >
            My Reviews
          </BreadcrumbLink>
        </BreadcrumbItem>
      )}

      {name && (
        <BreadcrumbItem>
          <BreadcrumbLink to={link} onClick={onClick}>
            {name}
          </BreadcrumbLink>
        </BreadcrumbItem>
      )}

      {isManagePageSource && (
        <BreadcrumbItem>
          <BreadcrumbLink to={null} onClick={() => history.goBack()}>
            Explore
          </BreadcrumbLink>
        </BreadcrumbItem>
      )}

      {!reviewNotVisible && (
        <BreadcrumbItem isCurrentPage>
          {selectedView === REVIEW_RESULT_VIEW.PEOPLE ? (
            <BreadcrumbLink>{displayName}</BreadcrumbLink>
          ) : (
            <BreadcrumbLink>{reviewCycleName}</BreadcrumbLink>
          )}
        </BreadcrumbItem>
      )}
    </BreadCrumb>
  );
};

const HeaderIntentBreadCrumb = ({ isLoading, data, reviewCycleName, reviewNotVisible = false }) => {
  const { displayName } = data ?? {};
  const selectedView = useSelectedReviewResultView();
  const { source, subSource, intentType } = useParsedQuery();

  const { data: view } = useViewFromQueryParam();

  const item3Label = selectedView === REVIEW_RESULT_VIEW.PEOPLE ? displayName : reviewCycleName;

  let firstLabel;
  let firstLabelTo;
  let secondLabelTo;
  const secondLabel = 'Reviews';

  const { moduleName } = useOrganisationSettings();

  const applicableL2ViewModules = getApplicableL2MyTeamModules('view', moduleName);

  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName) ?? [];
  const applicableL2DirectReportsModules = getApplicableL2MyTeamModules('directReports', moduleName);
  const applicableL2AllReportsModules = getApplicableL2MyTeamModules('allReports', moduleName);

  if (source === 'allReports') {
    firstLabel = 'Skip Team';
    firstLabelTo = applicableL2AllReportsModules[0]?.value;

    secondLabelTo = getSecondLabelTo({ source, intentType });
  } else if (source === 'directReports') {
    firstLabel = 'Direct Team';
    firstLabelTo = applicableL2DirectReportsModules[0]?.value;

    secondLabelTo = getSecondLabelTo({ source, intentType });
  } else if (source === 'view') {
    firstLabel = view?.name ?? '';
    firstLabelTo = `${applicableL2ViewModules[0]?.value}?viewId=${view?.id}`;

    secondLabelTo = `${getSecondLabelTo({ source, intentType })}?viewId=${view?.id}`;
  } else if (source === 'company') {
    firstLabel = 'Company';
    firstLabelTo = applicableL2CompanyModules[0]?.value;

    secondLabelTo = getSecondLabelTo({ source, intentType });
  } else {
    // case for My Data and default case

    firstLabel = 'Me';
    firstLabelTo = applicableL2MyDataModules[0]?.value;

    secondLabelTo = `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${
      subSource || REVIEW_TABLE_TYPES.MY_PAST_REVIEWS
    }`;
  }

  if (isLoading || !isNetchexLogin) {
    return null;
  }

  return (
    <DeerBreadcrumbs
      breadcrumbs={{
        items: [
          {
            label: firstLabel,

            to: firstLabelTo,
          },
          {
            label: secondLabel,
            to: secondLabelTo,
          },
        ],
        page: !reviewNotVisible
          ? {
              label: item3Label,
              to: '',
            }
          : null,
      }}
    />
  );
};

const Header = ({ isLoading, data, reviewCycleName, reviewNotVisible = false }) => {
  const { providerType } = useCurrentProvider() ?? {};
  const history = useHistory();
  const { displayName } = data ?? {};
  const selectedView = useSelectedReviewResultView();
  const { name } = REVIEW_CYCLE_EVENTS[providerType] ?? {};
  const { isPreview } = useReviewPreviewParams();
  const queryParams = useQueryParams();

  /**
   * Handles back navigation
   * - Goes to previous page if history exists
   * - Otherwise redirects based on deeplink.role (from notifications) or source query param:
   *   - Employee/Me -> Me > Reviews > Completed by me
   *   - Manager/Team -> Skip Team > Reviews > Completion status
   *   - Admin/Company -> Company > Reviews > Completion status
   * - Defaults to Me > Reviews > Completed by me
   */
  const goBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    const { 'deeplink.role': role, source } = queryParams;

    const completedByMePath = `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${REVIEW_TABLE_TYPES.COMPLETED_BY_ME}`;
    const allReportsCompletionPath = L3_PATHS.team.allReports.review.completionStatus;
    const companyCompletionPath = L3_PATHS.company.review.completionStatus;

    if (role === NETCHEX_ROLE.EMPLOYEE || source === 'Me') {
      history.push(completedByMePath);
    } else if (
      role === NETCHEX_ROLE.MANAGER ||
      source === 'directReports' ||
      source === 'allReports' ||
      source === 'view'
    ) {
      history.push(allReportsCompletionPath);
    } else if (role === NETCHEX_ROLE.ADMIN || source === 'company') {
      history.push(companyCompletionPath);
    } else {
      // Default redirect if no specific role/source match
      history.push(completedByMePath);
    }
  };

  if (isMobileView()) {
    return (
      <StyledPaper p="12px 16px" pl="14px">
        <Flex flexDir="column" gap={3}>
          <Flex alignItems="center">
            <Button mr="4px" StartIcon={ArrowBack} variant="tertiary" iconSize="24px" onClick={goBack} />
            <Box>
              <Flex mt="4px">
                {selectedView === REVIEW_RESULT_VIEW.PEOPLE ? (
                  <TextBodyMedium bold color="text.g2">
                    {name}
                  </TextBodyMedium>
                ) : (
                  <TextHeader1 bold color="text.g1">
                    Review Summary for {displayName}
                  </TextHeader1>
                )}
              </Flex>
            </Box>
          </Flex>
          {!reviewNotVisible && <EmailPDFReport />}
        </Flex>
      </StyledPaper>
    );
  }

  return (
    <HeaderMarginContainer
      wrapperProps={
        isNetchexLogin
          ? {
              bg: 'white',
              m: '8px 0 0 0',
              px: '12px',
              h: '64px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
            }
          : {}
      }
    >
      <StyledPaper p={isNetchexLogin ? '0' : '12px'} w="100%" bg={isNetchexLogin ? 'white !important' : undefined}>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex
            flexDirection={isNetchexLogin ? 'row' : 'column'}
            alignItems={isNetchexLogin ? 'center' : undefined}
            gap={isNetchexLogin ? '4px' : '0px'}
          >
            {!isNetchexLogin && (
              <Flex alignItems="center">
                {!isPreview && (
                  <Button mr="4px" StartIcon={ArrowBack} variant="tertiary" iconSize="24px" onClick={goBack} />
                )}
                {!isPreview && !checkIntentLandingPagesFeatureEnabled() && (
                  <HeaderBreadCrumb
                    isLoading={isLoading}
                    data={data}
                    reviewCycleName={reviewCycleName}
                    reviewNotVisible={reviewNotVisible}
                  />
                )}
                {!isPreview && checkIntentLandingPagesFeatureEnabled() && (
                  <HeaderIntentBreadCrumb
                    isLoading={isLoading}
                    data={data}
                    reviewCycleName={reviewCycleName}
                    reviewNotVisible={reviewNotVisible}
                  />
                )}
              </Flex>
            )}

            {isNetchexLogin && (
              <Button
                StartIcon={ArrowBack}
                variant="tertiary"
                onClick={goBack}
                startIconProps={{ color: '#666666 !important' }}
                style={{ padding: '0px' }}
              />
            )}

            <Box mt={isNetchexLogin ? '0' : '4px'} ml={isNetchexLogin ? '0' : '4px'}>
              {selectedView === REVIEW_RESULT_VIEW.PEOPLE ? (
                isNetchexLogin ? (
                  <TextHeader2 bold color="text.g1">
                    {name}
                  </TextHeader2>
                ) : (
                  <TextBodyMedium bold color="text.g2">
                    {name}
                  </TextBodyMedium>
                )
              ) : (
                <TextHeader2 bold color="text.g1">
                  Review Summary for {displayName}
                </TextHeader2>
              )}
            </Box>
          </Flex>
          {!reviewNotVisible && !isPreview && <EmailPDFReport />}
        </Flex>
      </StyledPaper>
    </HeaderMarginContainer>
  );
};

const ResultPageRabbit = () => {
  const { reviewId, employeeId, providerId } = useReviewResultParams();

  const [mockAcknowledgedOn, setMockAcknowledgedOn] = useState(null);

  const dispatch = useDispatch();
  const { defaultSelectedProviderId, autoNavigate } = useParsedQuery();

  const { data, isLoading } = useProfilePage({ employeeId });
  const reviewData = useReviewFormSidebarData({
    reviewId,
    employeeId,
    providerId,
  });

  useEffect(() => {
    if (autoNavigate || defaultSelectedProviderId) {
      // these query params are going to set the view and current provider,
      // so no need to reset state
      return;
    }
    dispatch(resetResultState());
  }, [autoNavigate, defaultSelectedProviderId, dispatch]);

  const { reviewInfo, isManagerFormAcknowledged, acknowledgedOn, employee } = reviewData?.data ?? {};
  const reviewCycleName = reviewInfo?.feedbackForm?.reviewCycle?.name;

  const acknowledgedOn1 = acknowledgedOn ?? mockAcknowledgedOn;

  if (!reviewData.isLoading && reviewData.isError) {
    return (
      <Flex flexDir="column" height="100%">
        <Header isLoading={isLoading} data={data} reviewCycleName={reviewCycleName} reviewNotVisible />
        <Paper px={6} py={2} mx={4} mb={4} flexGrow={1} display="flex" justifyContent="center" alignItems="center">
          <EmptyScreen
            heading="Nothing to see here, yet!"
            description="Manager is yet to submit the form, Please come back when the form is submitted by Manager"
            illustrationSrc={NoDataImg}
          />
        </Paper>
      </Flex>
    );
  }

  return (
    <>
      {!autoNavigate && <ScrollToTop />}

      {!checkSimplifiedReviewCreationEnabled() && <ReviewResultPreviewHeaderNote />}
      {checkSimplifiedReviewCreationEnabled() && <SwitchableReviewFormPreviewHeaderNote />}
      <Header isLoading={isLoading} data={data} reviewCycleName={reviewCycleName} />
      <Box p={checkNewNavEnabled() ? 0 : 4} pt={0}>
        <Box mb={4}>
          <PerformanceResultScore />
        </Box>
        {!isMobileView() && (
          <Flex gap={4} justifyContent="center">
            <ResultSpiderChart title="Competencies Spider Chart" />
          </Flex>
        )}

        <GoalResult />
        <CompetencyResult />
        <OverallQuestionsResult />
        {!isManagerFormAcknowledged && !checkEnableESignReviewReport() && <RabbitResultAcknowledge />}
        {!isManagerFormAcknowledged && checkEnableESignReviewReport() && (
          <RabbitResultDocusignAcknowledge setMockAcknowledgedOn={setMockAcknowledgedOn} />
        )}

        {acknowledgedOn1 && (
          <RabbitResultAcknowledgeInfo
            acknowledgedOn={acknowledgedOn1}
            acknowledgedBy={employee?.metadata?.displayName ?? ''}
          />
        )}
      </Box>
    </>
  );
};

export default ResultPageRabbit;
